import React from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Divider, Card } from '@material-ui/core';

import { ROUTE_DETAILS, ROUTE_STAKING } from 'constants/routes';

import { withdraw } from 'services/contracts/TimeLockPool';

import { formatToken, toUSD, calcRainAPR, calcRaiUniAPR } from 'helpers/utils';

import { ClaimActions } from 'store/reducers/claim';

import StatisticCell from './componets/StatisticCell';
import HeadCell from './componets/HeadCell';
import ButtonCell from './componets/ButtonCell';

import { useStyles } from './styles';
import { Decimal } from 'shared/components/Decimal';
import { VestingActions } from 'store/reducers/vesting';

interface PriceProps {
  statistic: any;
  type: string;
  index: number;
  address?: string;
  rewardPerSecond?: number | string;
  claimable?: boolean
}

const Statistic = (props: PriceProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { statistic, address, index, type, rewardPerSecond, claimable } = props;

  const withdrawDeposit = async (pool: string, depositId: string) => {
    if (address) {
      await withdraw(pool, address, depositId, address);
    }
  };

  let card = null;

  const rainEstimatedPrice = useSelector((state) => state.estimatedPrice.rainPrice);
  const lpEstimatedPrice = useSelector((state) => state.estimatedPrice.lpPrice);
  const LPLiquidity = useSelector((state) => state.pool.currentLPLiquidity);

  switch (type) {
    case 'pool':
      card = (
        <Box className={classes.content}>
          <StatisticCell
            title="Total Amount Staked"
            description={`$${toUSD(
              formatToken(statistic.poolTotalDeposit || '0', { decimalPlaces: 18 }),
            )}`}
          />
          <StatisticCell
            title="APR"
            description={
              index === 0
                ? calcRainAPR(+statistic?.poolTotalDeposit, rewardPerSecond || 0, statistic.weight)
                : calcRaiUniAPR(
                    rewardPerSecond || 0,
                    statistic.weight,
                    rainEstimatedPrice,
                    +statistic?.poolTotalDeposit,
                    lpEstimatedPrice,
                  )
            }
          />
          <Box className={classes.actions}>
            {index === 1 && (
              <ButtonCell
                color="primary"
                onClick={() =>
                  window.open(
                    'https://pancakeswap.finance/add/0x6Bcd897D4BA5675F860C7418ddc034f6c5610114/BNB',
                  )
                }
              >
                Add LP
              </ButtonCell>
            )}
            <Box className={classes.actionsGroup}>
              <ButtonCell
                variant="contained"
                color="secondary"
                onClick={() => history.push(`${ROUTE_DETAILS}/${statistic.poolAddress}`)}
              >
                Details
              </ButtonCell>
              <ButtonCell
                variant="contained"
                onClick={() => history.push(`${ROUTE_STAKING}/${statistic.poolAddress}`)}
              >
                Stake
              </ButtonCell>
            </Box>
          </Box>
        </Box>
      );
      break;
    case 'deposit':
      const pastTime = moment(new Date())
        .to(statistic.end * 1000)
        .includes('ago');

      card = (
        <Box className={classes.content}>
          <StatisticCell
            title="Amount Staked"
            description={`${formatToken(statistic.amount, { decimalPlaces: 18 })} ${
              statistic.depositTokenSymbol
            }`}
          />
          <StatisticCell
            title="Lock Date"
            description={moment(+statistic.start * 1000).format('lll')}
          />
          <StatisticCell
            title="Unlock Date"
            description={moment(+statistic.end * 1000).format('lll')}
          />
          <Box className={classes.actions}>
            <ButtonCell
              disabled={!pastTime}
              variant="contained"
              color="secondary"
              onClick={() => withdrawDeposit(statistic.poolAddress, statistic.depositId.toString())}
            >
              Withdraw
            </ButtonCell>
          </Box>
        </Box>
      );
      break;
    case 'rewards':
      card = (
        <Box className={classes.content}>
          <StatisticCell title="Core Pools" description={statistic.symbol} />
          <StatisticCell
            title="Amount Staked"
            description={`$${(
              toUSD(+formatToken(statistic.accountTotalDeposit || 0)) * statistic.estimatedPrice
            ).toFixed(2)}`}
          />
          <StatisticCell
            title="Claimable Rewards"
            description={`${+formatToken(statistic.accountPendingRewards || 0)} RAIN`}
          />
          <Box className={classes.actions}>
            <ButtonCell
              variant="contained"
              onClick={() =>
                dispatch(
                  ClaimActions.claimRequest({
                    receiver: address,
                    poolAddress: statistic.poolAddress,
                    claimId: index,
                  }),
                )
              }
            >
              Claim
            </ButtonCell>
          </Box>
        </Box>
      );
      break;
    case 'investment': {
      card = (
        <Box className={classes.content}>
          <StatisticCell
            title="Locked RAIN"
            description={`${Decimal.format(statistic.amount || 0, 10, ',')} RAIN`}
          />
          <StatisticCell
            title="Claimable RAIN"
            description={`${Decimal.format(statistic.claimable || 0, 10, ',')} RAIN`}
          />
          <Box className={classes.actions}>
            <ButtonCell
              variant="contained"
              disabled={claimable}
              onClick={() => dispatch(VestingActions.vestingClaimRequest({ address: address as string, type: 'kol' }))}
            >
              Claim
            </ButtonCell>
          </Box>
        </Box>
      );
      break;
    }
    case 'launchpad': {
      card = (
        <Box className={classes.content}>
          <StatisticCell
            title="Locked RAIN"
            description={`${Decimal.format(statistic.amount || 0, 10, ',')} RAIN`}
          />
          <StatisticCell
            title="Claimable RAIN"
            description={`${Decimal.format(statistic.claimable || 0, 10, ',')} RAIN`}
          />
          <Box className={classes.actions}>
            <ButtonCell
              variant="contained"
              onClick={() => dispatch(VestingActions.vestingClaimRequest({ address: address as string, type: 'launchpads' }))}
            >
              Claim
            </ButtonCell>
          </Box>
        </Box>
      );
      break;
    }
    default:
      card = (
        <Box className={classes.content}>
          <StatisticCell title="Token" description="" />
          <StatisticCell title="Amount" description="" />
          <StatisticCell title="Dollar Value" description="" />
          <StatisticCell title="Status" description="" />
          <StatisticCell title="Time Remaining" description="" />
        </Box>
      );
      break;
  }

  return (
    <Card className={classes.card}>
      <HeadCell title={statistic.symbol === 'sRAIN' ? 'RAIN' : statistic.symbol} />
      <Divider className={classes.divider} />
      {card}
    </Card>
  );
};

export default Statistic;
